import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Elements } from "@stripe/react-stripe-js";
import {
  Appearance,
  loadStripe,
  StripeElementsOptions,
} from "@stripe/stripe-js";
import styled from "styled-components";

import { State } from "../../types";
import { ButtonStyles } from "../../types/styles";
import {
  Logo,
  Title,
  IconButton,
  Modal,
  LoadingSpinner,
} from "../../components";

import { ArrowLeftSolid } from "../../assets/icons";
import {
  BookingResponse,
  CreateBookingStatus,
} from "../../domain_objects/BookingResponse";
import DateHelper from "../../helpers/DateHelper";
import WidgetService from "../../services/WidgetService";
import { defaultThemeStyles } from "../../mocks";
import mockWidgetService from "../../mocks/mockWidgetService";
import StripePaymentElement from "./StripeElement/StripePaymentElement";
import StripeElement from "./StripeElement/StripeElement";
import SelectedVenueDetailsHelper from "../../helpers/SelectedVenueDetailsHelper";

const StyledPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledHeader = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr 30px;
  justify-content: center;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  flex-grow: 1;
`;

const StyledWarning = styled.div<{
  centerText?: boolean;
}>`
  color: #8a6d3b;
  background-color: #fcf8e3;
  border-color: #faebcc;
  padding: 15px;
  margin-bottom: 18px;
  border: 1px solid transparent;
  border-radius: 3px;
  color: #a94442;
  margin: 0px;
  font-size: 13px;
  text-align: ${({ centerText }) => (centerText ? "center" : "left")};
`;

export type PaymentPageProps = {
  state: State;
  nextPage: (
    payload: Partial<State>,
    stripeAuthCallbackFunc?: (
      paymentMethodId: string,
      paymentIntentSecret: string
    ) => void,
    handleStripeErrorFunc?: () => void
  ) => void;
  prevPage: (payload: Partial<State>) => void;
  styles: ButtonStyles;
  widgetService: WidgetService | mockWidgetService;
};

export const PaymentPage: React.FC<PaymentPageProps> = ({
  state,
  nextPage,
  prevPage,
  styles,
  widgetService,
}) => {
  const { t } = useTranslation();
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [showIsStillProcessingMessage, setShowIsStillProcessingMessage] =
    useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (showSpinner) {
      setTimeout(() => {
        setShowIsStillProcessingMessage(true);
      }, 10000);
    } else {
      setShowIsStillProcessingMessage(false);
    }
  }, [showSpinner]);

  const handleStripeError = () => {
    setShowSpinner(false);
    setShowIsStillProcessingMessage(false);
    setIsErrorModalOpen(true);
  };

  const makeBooking = async (
    paymentMethodId?: string | undefined,
    paymentIntentId?: string | undefined,
    confirmationTokenId?: string | undefined,
    stripeAuthCallbackFunc?: (
      paymentIntentSecret: string,
      paymentMethodId?: string,
      confirmationTokenId?: string
    ) => void
  ) => {
    setShowIsStillProcessingMessage(false);
    setShowSpinner(true);
    await widgetService.createBookingWithConfirmationToken(
      state.hostname!,
      state.bookingDetails.timeSlot!.MicrositeName!,
      {
        ChannelCode: state.channelCode!,
        Customer: state.contactDetails!,
        Reference: undefined,
        RestaurantName: undefined,
        VisitDate: DateHelper.getDateString(state.bookingDetails.selectedDate),
        VisitTime: DateHelper.sliceTimeSlot(state.bookingDetails.timeSlot!),
        PartySize: state.bookingDetails.partySize,
        SpecialRequests: state.contactDetails!.SpecialRequests,
        PromotionId: state.promotionDetails?.promotionId,
        IpAddress: undefined,
        IsLeaveTimeConfirmed:
          state.bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired,
        ConciergeId: undefined,
        ConciergeEmail: undefined,
        ConciergeCustomerId: undefined,
        Token: undefined,
        PaymentInformation: undefined,
        AreaId: undefined,
        PaymentMethodId: paymentMethodId,
        PaymentIntentId: paymentIntentId,
        SetupIntentId: undefined,
        RoomNumber: undefined,
        PreorderGuestFormUrl: undefined,
        DeliveryAddress: undefined,
        Households: state.contactDetails?.Households,
        IsWidgetBooking: true,
        MicrositeName: state.bookingDetails.timeSlot!.MicrositeName!,
        OriginalMicrositeName: state.micrositeName!,
        WidgetThemeId: state.themeId!,
        ConfirmationTokenId: confirmationTokenId,
      }
    ).then((response: BookingResponse) => {
      if (
        response.Status === CreateBookingStatus.StripeAuthenticationRequired &&
        stripeAuthCallbackFunc 
      ) {
        if (paymentMethodId) {
          stripeAuthCallbackFunc(response.PaymentIntentSecret, paymentMethodId, undefined);
        } else if (confirmationTokenId) {
          stripeAuthCallbackFunc(response.PaymentIntentSecret, undefined, confirmationTokenId);
        }
      } else if (
        (response.Status === CreateBookingStatus.PaymentFailed ||
          response.Status === CreateBookingStatus.StripeError) &&
        handleStripeError
      ) {
        handleStripeError();
      } else {
        const updatedState = {
          ...state,
          paymentDetails: {
            paymentMethodId,
            paymentIntentId,
          },
          bookingConfirmationDetails: response,
        };
        nextPage(updatedState);
      }
      setShowSpinner(false);
    });
  };


  const makeBookingWithConfirmationTokenId = async (confirmationTokenId: string, paymentIntentId?: string, setupIntentId?: string, stripeAuthCallbackFunc?: (confirmationTokenId: string, paymentIntentSecret?: string, setupIntentSecret?: string) => void) => {
    setShowIsStillProcessingMessage(false);
    setShowSpinner(true);
    await widgetService.createBookingWithConfirmationToken(
      state.hostname!,
      state.bookingDetails.timeSlot!.MicrositeName!,
      {
        ChannelCode: state.channelCode!,
        Customer: state.contactDetails!,
        Reference: undefined,
        RestaurantName: undefined,
        VisitDate: DateHelper.getDateString(state.bookingDetails.selectedDate),
        VisitTime: DateHelper.sliceTimeSlot(state.bookingDetails.timeSlot!),
        PartySize: state.bookingDetails.partySize,
        SpecialRequests: state.contactDetails!.SpecialRequests,
        PromotionId: state.promotionDetails?.promotionId,
        IpAddress: undefined,
        IsLeaveTimeConfirmed:
          state.bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired,
        ConciergeId: undefined,
        ConciergeEmail: undefined,
        ConciergeCustomerId: undefined,
        Token: undefined,
        PaymentInformation: undefined,
        AreaId: undefined,
        PaymentMethodId: undefined,
        PaymentIntentId: paymentIntentId,
        SetupIntentId: setupIntentId,
        RoomNumber: undefined,
        PreorderGuestFormUrl: undefined,
        DeliveryAddress: undefined,
        Households: state.contactDetails?.Households,
        IsWidgetBooking: true,
        MicrositeName: state.bookingDetails.timeSlot!.MicrositeName!,
        OriginalMicrositeName: state.micrositeName!,
        WidgetThemeId: state.themeId!,
        ConfirmationTokenId: confirmationTokenId
      }
    ).then((response: BookingResponse) => {
      if (
        response.Status === CreateBookingStatus.StripeAuthenticationRequired &&
        stripeAuthCallbackFunc
      ) {
        response.PaymentIntentSecret ? stripeAuthCallbackFunc(confirmationTokenId, response.PaymentIntentSecret, undefined) : stripeAuthCallbackFunc(confirmationTokenId, undefined, response.SetupIntentClientSecret)
      } else if (
        (response.Status === CreateBookingStatus.PaymentFailed ||
          response.Status === CreateBookingStatus.StripeError) &&
        handleStripeError
      ) {
        handleStripeError();
      } else {
        const updatedState = {
          ...state,
          bookingConfirmationDetails: response,
        };
        nextPage(updatedState);
      }
      setShowSpinner(false);
    });
  }

  const backButtonClick = () => {
    prevPage(state);
  };

  const stripePromise = loadStripe(
    state.bookingConfirmationDetails?.StripePublishableKey ?? "",
    state.bookingConfirmationDetails?.StripeAccountId
      ? { stripeAccount: state.bookingConfirmationDetails.StripeAccountId }
      : undefined
  );

  const getElementOptions: () => StripeElementsOptions = () => {
    const isCreditCardRequired =
      state.bookingConfirmationDetails?.Status ===
      CreateBookingStatus.CreditCardRequired;
    const isPaymentRequired =
      state.bookingConfirmationDetails?.Status ===
      CreateBookingStatus.PaymentRequired;

    const appearance: Appearance = {
      variables: {
        borderRadius:
          state.crossSellSetup?.ThemeStyles?.DropdownBorderRadius !== undefined
            ? state.crossSellSetup.ThemeStyles.DropdownBorderRadius.toString() +
              "px"
            : defaultThemeStyles.DropdownBorderRadius.toString() + "px",
        colorPrimary:
          state.crossSellSetup?.ThemeStyles?.OverallGeneralTextColour ||
          defaultThemeStyles.OverallGeneralTextColour,
        colorTextSecondary:
          state.crossSellSetup?.ThemeStyles?.OverallGeneralTextColour ||
          defaultThemeStyles.OverallGeneralTextColour,
        colorText:
          state.crossSellSetup?.ThemeStyles?.DropdownGeneralTextColour ||
          defaultThemeStyles.DropdownGeneralTextColour,
        colorBackground:
          state.crossSellSetup?.ThemeStyles?.DropdownGeneralBackgroundColour ||
          defaultThemeStyles.DropdownGeneralBackgroundColour,
        colorTextPlaceholder:
          state.crossSellSetup?.ThemeStyles?.DropdownGeneralPlaceholderColour ||
          defaultThemeStyles.DropdownGeneralPlaceholderColour,
        colorWarning:
          state.crossSellSetup?.ThemeStyles
            ?.DropdownGeneralValidationErrorColour ||
          defaultThemeStyles.DropdownGeneralValidationErrorColour,
        colorDanger:
          state.crossSellSetup?.ThemeStyles
            ?.DropdownGeneralValidationErrorColour ||
          defaultThemeStyles.DropdownGeneralValidationErrorColour,
        fontFamily:
          state.crossSellSetup?.ThemeStyles?.OverallGeneralFont ||
          defaultThemeStyles.OverallGeneralFont,
      },
      rules: {
        ".Tab": {
          border:
            state.crossSellSetup?.ThemeStyles?.DropdownBorderColour !==
            undefined
              ? "1px solid " +
                state.crossSellSetup?.ThemeStyles?.DropdownBorderColour
              : "1px solid " + defaultThemeStyles.DropdownBorderColour,
        },

        ".Tab--selected": {
          border:
            state.crossSellSetup?.ThemeStyles?.DropdownBorderFocusColour !==
            undefined
              ? "1px solid " +
                state.crossSellSetup?.ThemeStyles?.DropdownBorderFocusColour
              : "1px solid " + defaultThemeStyles.DropdownBorderFocusColour,
        },

        ".Input": {
          border:
            state.crossSellSetup?.ThemeStyles?.DropdownBorderColour !==
            undefined
              ? "1px solid " +
                state.crossSellSetup?.ThemeStyles?.DropdownBorderColour
              : "1px solid " + defaultThemeStyles.DropdownBorderColour,
          color:
            state.crossSellSetup?.ThemeStyles?.DropdownGeneralTextColour ||
            defaultThemeStyles.DropdownGeneralTextColour,
        },

        ".Input:focus": {
          border:
            state.crossSellSetup?.ThemeStyles?.DropdownBorderFocusColour !==
            undefined
              ? "1px solid " +
                state.crossSellSetup?.ThemeStyles?.DropdownBorderFocusColour
              : "1px solid " + defaultThemeStyles.DropdownBorderFocusColour,
        },

        ".Input--invalid": {
          borderColor:
            state.crossSellSetup?.ThemeStyles
              ?.DropdownGeneralValidationErrorColour ||
            defaultThemeStyles.DropdownGeneralValidationErrorColour,
        },

        ".Error": {
          color:
            state.crossSellSetup?.ThemeStyles
              ?.DropdownGeneralValidationErrorColour ||
            defaultThemeStyles.DropdownGeneralValidationErrorColour,
        },

        ".Label": {
          fontSize: "13px",
          fontWeight: "700",
          color:
            state.crossSellSetup?.ThemeStyles?.OverallGeneralTextColour ||
            defaultThemeStyles.OverallGeneralTextColour,
        },
      },
    };

    if (isCreditCardRequired && !isPaymentRequired) {
      return {
        mode: "setup",
        currency: state.crossSellSetup?.CurrencyCode.toLowerCase(),
        appearance: appearance,
        paymentMethodCreation: 'manual',
        fonts: [
          {
            cssSrc:
              "https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Arya:wght@400;700&family=Josefin+Slab:ital,wght@0,100..700;1,100..700&family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&family=Montserrat:ital,wght@0,100..900;1,100..900&family=Old+Standard+TT:ital,wght@0,400;0,700;1,400&family=Open+Sans:ital,wght@0,300..800;1,300..800&family=Oswald:wght@200..700&family=Oxygen:wght@300;400;700&family=PT+Sans:ital,wght@0,400;0,700;1,400;1,700&family=Raleway:ital,wght@0,100..900;1,100..900&family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&family=Vollkorn:ital,wght@0,400..900;1,400..900&display=swap",
          },
          {
            cssSrc: "https://use.typekit.net/xzj6yjv.css",
          },
        ],
      };
    } else {
      return {
        mode: "payment",
        currency: state.crossSellSetup?.CurrencyCode.toLowerCase(),
        amount:
          state.bookingConfirmationDetails?.PaymentAmount !== null
            ? state.bookingConfirmationDetails?.PaymentAmount
            : undefined,
        paymentMethodCreation: "manual",
        appearance: appearance,
        fonts: [
          {
            cssSrc:
              "https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Arya&family=Droid+Sans&family=Josefin&family=Lato&family=Montserrat&family=Old+Standard+TT&family=Open+Sans&family=Oswald&family=Oxygen&family=PT+Sans&family=Raleway&family=Ubuntu&family=Vollkorn:ital,wght@0,400;0,500;0,700;0,900;1,400;1,500;1,700;1,900&display=swap",
          },
        ],
      };
    }
  };

  return (
    <StyledPageContainer>
      <Logo styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles} />
      {!showSpinner && (
        <>
          <StyledHeader>
            <IconButton
              ariaLabel={t("Back")}
              icon={ArrowLeftSolid}
              onClick={backButtonClick}
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              dataId="back-button"
            />
            <StyledTitle
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
            >
              {t("CardDetails")}
            </StyledTitle>
          </StyledHeader>
          {state.crossSellSetup?.UseConfirmationTokenStripeFlow ? (
            <Elements stripe={stripePromise} options={getElementOptions()}>
              {!state.crossSellSetup?.StripeInfo.UseLiveToken && (
                <StyledWarning data-id="stripe-test-mode-warning">
                  {t("StripeTestModeWarning")}
                </StyledWarning>
              )}
              <StripePaymentElement
                state={state}
                makeBookingWithConfirmationTokenId={makeBookingWithConfirmationTokenId}
                isCreditCardRequired={
                  state.bookingConfirmationDetails?.Status ===
                  CreateBookingStatus.CreditCardRequired
                }
                handleStripeError={handleStripeError}
                hostname={state.hostname || ""}
                restaurantId={state.crossSellSetup?.RestaurantId}
                styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
                bookingSummary={{
                  numberOfCovers: state.bookingDetails.partySize,
                  date: state.bookingDetails.selectedDate,
                  timeslot: DateHelper.sliceTimeSlot(
                    state.bookingDetails.timeSlot!
                  ),
                  styles:
                    state.crossSellSetup?.ThemeStyles || defaultThemeStyles,
                }}
                isCrossSellChosen={state.isCrossSellChosen}
                formattedPaymentAmount={
                  state.bookingConfirmationDetails?.FormattedPaymentAmount
                }
                venueInfo={{
                  providerName:
                    SelectedVenueDetailsHelper.getVenueDetails(state).name ||
                    "",
                  providerLocation:
                    SelectedVenueDetailsHelper.getVenueDetails(state)
                      .location || "",
                  providerLocationUrl:
                    SelectedVenueDetailsHelper.getVenueDetails(state).url || "",
                  styles:
                    state.crossSellSetup?.ThemeStyles || defaultThemeStyles,
                }}
              />
            </Elements>
          ) : (
            <Elements stripe={stripePromise}>
              {!state.crossSellSetup?.StripeInfo.UseLiveToken && (
                <StyledWarning data-id="stripe-test-mode-warning">
                  {t("StripeTestModeWarning")}
                </StyledWarning>
              )}
              <StripeElement
                state={state}
                styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
                makeBooking={makeBooking}
                clientSecret={
                  state.bookingConfirmationDetails?.SetupIntentClientSecret
                }
                isCreditCardRequired={
                  state.bookingConfirmationDetails?.Status ===
                  CreateBookingStatus.CreditCardRequired
                }
                isPaymentRequired={
                  state.bookingConfirmationDetails?.Status ===
                  CreateBookingStatus.PaymentRequired
                }
                formattedPaymentAmount={
                  state.bookingConfirmationDetails?.FormattedPaymentAmount
                }
                venueInfo={{
                  providerName:
                    SelectedVenueDetailsHelper.getVenueDetails(state).name ||
                    "",
                  providerLocation:
                    SelectedVenueDetailsHelper.getVenueDetails(state)
                      .location || "",
                  providerLocationUrl:
                    SelectedVenueDetailsHelper.getVenueDetails(state).url || "",
                  styles:
                    state.crossSellSetup?.ThemeStyles || defaultThemeStyles,
                }}
                bookingSummary={{
                  numberOfCovers: state.bookingDetails.partySize,
                  date: state.bookingDetails.selectedDate,
                  timeslot: DateHelper.sliceTimeSlot(
                    state.bookingDetails.timeSlot!
                  ),
                  styles:
                    state.crossSellSetup?.ThemeStyles || defaultThemeStyles,
                }}
                handleStripeError={handleStripeError}
                isCrossSellChosen={state.isCrossSellChosen}
                hostname={state.hostname || ""}
                restaurantId={state.crossSellSetup?.RestaurantId}
              />
            </Elements>
          )}
          <Modal
            setIsModalOpen={setIsErrorModalOpen}
            isModalOpen={isErrorModalOpen}
            modalTitleText={t("TransactionFailed")}
            modalBodyText={t("TransactionFailedMessage")}
            closeModalButtonText={t("Ok")}
            styles={styles}
          />
        </>
      )}
      {showSpinner && (
        <>
          <LoadingSpinner hostname={state.hostname ?? ""} />
          <StyledWarning
            dangerouslySetInnerHTML={{ __html: t("StripeProcessingMessage") }}
            centerText
            data-id="stripe-processing-message"
          />
          {showIsStillProcessingMessage && (
            <StyledWarning
              dangerouslySetInnerHTML={{
                __html: t("StripeStillProcessingMessage"),
              }}
              centerText
              data-id="stripe-still-processing-message"
            />
          )}
        </>
      )}
    </StyledPageContainer>
  );
};

export default PaymentPage;
